import React, { useState } from 'react';
import { ShowcaseSection, ShowcaseItem } from '../ShowcaseItem';
import { Progress } from '@/components/custom/progress/progress';
import { ProgressCircular } from '@/components/custom/progress/progress-circular';
import { ProgressSemicircle } from '@/components/custom/progress/progress-semicircle';
import { KpiWrapper, KpiCard, KpiCardTitle, KpiCardValue, KpiCardMetrics, KpiCardMetric, KpiCardExplanation } from '@/components/custom/kpi/kpi';
import { MetricCard, MetricCardTitle, MetricCardBody, MetricCardDescription } from '@/components/custom/metric/metric-card';
import { StarRating } from '@/components/custom/star-rating/star-rating';
import { MedalContainer } from '@/components/custom/medal/medal-container';
import { MetricSection, MetricHat } from '@/components/custom/metric/metric-section';
import { SkillEvaluation, SkillEvaluationHeader, SkillEvaluationBody, SkillEvaluationCol } from '@/components/custom/skill/skill-evaluation';
import { Badge } from '@/components/ui/badge';

export function ProgressSection() {
  const [rate, setRate] = useState(3);

  return (
    <ShowcaseSection
      id="progress"
      title="Progress, Metrics & KPIs"
      description="Indicadores de progresso, KPIs, metricas, rating e medalhas"
    >
      <ShowcaseItem
        label="Progress"
        description="Barra de progresso linear"
      >
        <div className="space-y-3">
          <Progress
            valueNow={25}
            showText
          />
          <Progress
            valueNow={60}
            showText
          />
          <Progress
            valueNow={90}
            showText
          />
          <Progress valueNow={45} />
        </div>
      </ShowcaseItem>

      <ShowcaseItem
        label="ProgressCircular"
        description="Indicador circular tipo donut"
      >
        <div className="flex flex-wrap items-end gap-6">
          <div className="flex flex-col items-center gap-2">
            <ProgressCircular
              valueNow={25}
              className="progress-circular--small"
            />
            <code className="text-[10px] text-neutral-600">small / 25%</code>
          </div>
          <div className="flex flex-col items-center gap-2">
            <ProgressCircular valueNow={60} />
            <code className="text-[10px] text-neutral-600">default / 60%</code>
          </div>
          <div className="flex flex-col items-center gap-2">
            <ProgressCircular
              valueNow={92}
              className="progress-circular--md"
            />
            <code className="text-[10px] text-neutral-600">md / 92%</code>
          </div>
        </div>
      </ShowcaseItem>

      <ShowcaseItem
        label="ProgressSemicircle"
        description="Medidor semicircular"
      >
        <div className="flex flex-wrap items-end gap-8">
          <ProgressSemicircle value={30} />
          <ProgressSemicircle value={65} />
          <ProgressSemicircle value={95} />
        </div>
      </ShowcaseItem>

      <ShowcaseItem
        label="KpiCard"
        description="Cartao KPI com titulo, valor e tendencia"
      >
        <KpiWrapper>
          <KpiCard>
            <KpiCardTitle>Alunos ativos</KpiCardTitle>
            <KpiCardValue>1.234</KpiCardValue>
            <KpiCardMetrics>
              <KpiCardMetric trend="positive">+12% vs mes anterior</KpiCardMetric>
            </KpiCardMetrics>
            <KpiCardExplanation>Total de alunos com acesso nos ultimos 30 dias</KpiCardExplanation>
          </KpiCard>
          <KpiCard>
            <KpiCardTitle>Taxa de evasao</KpiCardTitle>
            <KpiCardValue>8,2%</KpiCardValue>
            <KpiCardMetrics>
              <KpiCardMetric trend="negative">+2% vs mes anterior</KpiCardMetric>
            </KpiCardMetrics>
            <KpiCardExplanation>Alunos que nao acessaram nos ultimos 14 dias</KpiCardExplanation>
          </KpiCard>
          <KpiCard>
            <KpiCardTitle>Conclusoes</KpiCardTitle>
            <KpiCardValue>342</KpiCardValue>
            <KpiCardMetrics>
              <KpiCardMetric>Sem variacao</KpiCardMetric>
            </KpiCardMetrics>
            <KpiCardExplanation>Cursos concluidos no periodo</KpiCardExplanation>
          </KpiCard>
        </KpiWrapper>
      </ShowcaseItem>

      <ShowcaseItem
        label="MetricCard"
        description="Cartao de metrica detalhada"
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <MetricCard>
            <MetricCardTitle>Engajamento Geral</MetricCardTitle>
            <MetricCardBody>
              <span className="text-2xl font-extrabold text-primary">87%</span>
              <MetricCardDescription>Indice de engajamento agregado dos ultimos 30 dias.</MetricCardDescription>
            </MetricCardBody>
          </MetricCard>
          <MetricCard>
            <MetricCardTitle>Tempo medio de aula</MetricCardTitle>
            <MetricCardBody>
              <span className="text-2xl font-extrabold text-primary">42min</span>
              <MetricCardDescription>Duracao media das aulas assistidas pelos alunos.</MetricCardDescription>
            </MetricCardBody>
          </MetricCard>
        </div>
      </ShowcaseItem>

      <ShowcaseItem
        label="StarRating"
        description="Avaliacao por estrelas (interativa)"
      >
        <div className="flex flex-col gap-3">
          <StarRating
            rate={rate}
            setRate={setRate}
            onClick={() => {}}
          />
          <p className="text-xs text-neutral-dark">
            Avaliacao: <strong className="text-primary">{rate || 0}</strong> de 5
          </p>
        </div>
      </ShowcaseItem>

      <ShowcaseItem
        label="MetricSection — 4 variantes"
        description="Sections temadas com hat label flutuante (primary, conquest, mission, merit)"
      >
        <div className="grid gap-6 lg:grid-cols-2">
          <MetricSection variant="primary">
            <MetricHat>Primary</MetricHat>
            <p className="text-sm text-neutral-dark">Metricas principais do perfil. 75% das atividades concluidas.</p>
          </MetricSection>
          <MetricSection variant="conquest">
            <MetricHat>Conquistas</MetricHat>
            <p className="text-sm text-neutral-dark">12 conquistas desbloqueadas neste mes.</p>
          </MetricSection>
          <MetricSection variant="mission">
            <MetricHat>Missoes</MetricHat>
            <p className="text-sm text-neutral-dark">3 de 5 missoes da semana completas.</p>
          </MetricSection>
          <MetricSection variant="merit">
            <MetricHat>Meritos</MetricHat>
            <p className="text-sm text-neutral-dark">8 meritos especiais reconhecidos.</p>
          </MetricSection>
        </div>
      </ShowcaseItem>

      <ShowcaseItem
        label="SkillEvaluation"
        description="Painel de avaliacao com header + body de 2 colunas (rubrica + nota)"
      >
        <SkillEvaluation>
          <SkillEvaluationHeader>
            <h3 className="m-0 text-base font-bold text-primary-dark">Avaliacao de competências</h3>
            <p className="m-0 text-sm text-neutral">Baseado nas atividades do bimestre</p>
          </SkillEvaluationHeader>
          <SkillEvaluationBody>
            <SkillEvaluationCol>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-primary-dark">Logica Matematica</span>
                  <Badge variant="success">9.2</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-primary-dark">Interpretacao de Texto</span>
                  <Badge variant="warning">7.5</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-primary-dark">Pensamento Critico</span>
                  <Badge variant="success">8.8</Badge>
                </div>
              </div>
            </SkillEvaluationCol>
            <SkillEvaluationCol>
              <div className="text-center">
                <div className="text-5xl font-extrabold text-primary">8.5</div>
                <div className="text-sm text-neutral">Media geral</div>
              </div>
            </SkillEvaluationCol>
          </SkillEvaluationBody>
        </SkillEvaluation>
      </ShowcaseItem>

      <ShowcaseItem
        label="MedalContainer"
        description="6 variantes de medalha (default, primary, success, warning, danger, gold)"
      >
        <div className="flex flex-wrap items-end gap-6">
          {['default', 'primary', 'success', 'warning', 'danger', 'gold'].map(src => (
            <div
              key={src}
              className="flex flex-col items-center gap-2"
            >
              <MedalContainer
                value="92"
                src={src}
              />
              <code className="text-[10px] text-neutral-600">{src}</code>
            </div>
          ))}
        </div>
      </ShowcaseItem>
    </ShowcaseSection>
  );
}
